import type { CrowdexReferenceKind } from '@crowdex/agent-contracts';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@crowdex/ui/ai/conversation';
import { Button } from '@crowdex/ui/components/button';
import { ArrowUp, MessageSquare, Share, Square } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  ConversationActionsProvider,
  ConversationConnectionPrompts,
  ConversationMessageStream,
} from '@/components/conversation-pane-parts';
import { PanelEmpty } from '@/components/desktop-panel-primitives';
import { useConversationRuntime } from '@/hooks/use-conversation-runtime';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { messageText } from '@/lib/conversations/message-part/message-text';
import type { NativeAgent } from '@/lib/native-desktop';

/**
 * The side chat's field (D52): one line to ask on the side, sent with ↵ or its button, which
 * stops the answer while it comes. Its agent is the conversation's: it has no agent picker.
 */
export function SideChatComposer({
  value,
  busy,
  disabled,
  onChange,
  onSubmit,
  onStop,
}: {
  value: string;
  busy: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
}) {
  return (
    <form
      className="flex h-12 items-center gap-2 rounded-composer border border-border bg-card pr-1.5 pl-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <input
        aria-label="Ask a side question"
        className="type-ui min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder={disabled && !busy ? 'Starting the agent…' : 'Ask a side question…'}
        value={value}
      />
      {busy ? (
        <button
          aria-label="Stop"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-control text-foreground"
          onClick={onStop}
          type="button"
        >
          <Square className="size-3.5" />
        </button>
      ) : (
        <button
          aria-label="Send"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-control text-foreground disabled:opacity-40"
          disabled={disabled || value.trim().length === 0}
          type="submit"
        >
          <ArrowUp className="size-4" />
        </button>
      )}
    </form>
  );
}

/**
 * The side chat as a panel tab (D52, mobile 50): a question about the conversation, answered on the
 * side without interrupting its agent, and a reply that can be sent to that agent.
 */
export function SideChatLayout({
  children,
  composer,
}: {
  children: ReactNode;
  composer: ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <p className="type-meta shrink-0 px-4 pt-1 pb-2 text-muted-foreground">
        Ask about this conversation without interrupting the agent.
      </p>
      <Conversation className="min-h-0 flex-1">
        <ConversationContent className="mx-auto min-h-full w-full max-w-xl gap-4 px-4 py-2">
          {children}
        </ConversationContent>
        <ConversationScrollButton className="bottom-3 size-8 border-border bg-card shadow-lg" />
      </Conversation>
      <div className="shrink-0 px-4 pt-3 pb-4">
        <div className="mx-auto max-w-xl">{composer}</div>
      </div>
    </div>
  );
}

export function SideChatPanel({
  agent,
  cloudRuntime,
  prompt,
  workspace,
  resolveReference,
  onSendToAgent,
}: {
  agent: NativeAgent | null;
  cloudRuntime: CloudRuntime | null;
  prompt: { autoSubmit: boolean; id: string; text: string } | null;
  workspace: string | null;
  resolveReference: (kind: CrowdexReferenceKind, id: string) => Promise<unknown>;
  /** Sends a side answer to the conversation's own agent. */
  onSendToAgent?: ((text: string) => void) | undefined;
}) {
  const [restartGeneration, setRestartGeneration] = useState(0);
  const sessionScope = `${workspace ?? 'no-workspace'}:${agent?.id ?? 'no-agent'}:${restartGeneration}`;

  return (
    <SideChatSession
      agent={agent}
      cloudRuntime={cloudRuntime}
      key={sessionScope}
      onSendToAgent={onSendToAgent}
      onRestart={() => setRestartGeneration((generation) => generation + 1)}
      prompt={prompt}
      workspace={workspace}
      resolveReference={resolveReference}
    />
  );
}

function SideChatSession({
  agent,
  cloudRuntime,
  onRestart,
  prompt,
  workspace,
  resolveReference,
  onSendToAgent,
}: {
  agent: NativeAgent | null;
  cloudRuntime: CloudRuntime | null;
  onSendToAgent?: ((text: string) => void) | undefined;
  onRestart: () => void;
  prompt: { autoSubmit: boolean; id: string; text: string } | null;
  workspace: string | null;
  resolveReference: (kind: CrowdexReferenceKind, id: string) => Promise<unknown>;
}) {
  const [conversationId] = useState(() => crypto.randomUUID());
  const { connection, chat, busy, stopGeneration } = useConversationRuntime({
    conversationId,
    agent,
    cloudRuntime,
    workspaceRoot: workspace,
    resolveReference,
  });
  const { addToolApprovalResponse, messages, sendMessage, status, setMessages } = chat;
  const [draft, setDraft] = useState('');
  const submittedPromptRef = useRef<string | null>(null);
  useEffect(() => {
    if (prompt !== null && !prompt.autoSubmit) setDraft(prompt.text);
  }, [prompt]);
  const ready =
    connection.status.state === 'ready' &&
    connection.status.state === 'ready' &&
    status !== 'submitted' &&
    status !== 'streaming';

  useEffect(() => {
    if (prompt === null || !prompt.autoSubmit || !ready || submittedPromptRef.current === prompt.id)
      return;
    submittedPromptRef.current = prompt.id;
    setDraft('');
    void sendMessage({ text: prompt.text });
  }, [prompt, ready, sendMessage]);

  function submit(): void {
    const text = draft.trim();
    if (!ready || text.length === 0) return;
    setDraft('');
    void sendMessage({ text });
  }

  const lastReply = messages.findLast((message) => message.role === 'assistant');
  const sendConversationAction = (text: string) => {
    void sendMessage({ text });
  };

  return (
    <ConversationActionsProvider
      busy={busy}
      messages={messages}
      onSend={sendConversationAction}
      ready={connection.status.state === 'ready'}
      respondToInteraction={connection.respondToInteraction}
      setMessages={setMessages}
    >
      <SideChatLayout
        composer={
          <SideChatComposer
            busy={busy}
            disabled={agent === null || workspace === null || !ready}
            onChange={setDraft}
            onStop={() => void stopGeneration()}
            onSubmit={submit}
            value={draft}
          />
        }
      >
        {agent === null || workspace === null ? (
          <PanelEmpty
            icon={MessageSquare}
            text="Open a project and select an agent to start a side chat."
            title="Side chat unavailable"
          />
        ) : messages.length === 0 ? (
          connection.status.state === 'ready' ? null : (
            <p className="type-meta text-muted-foreground">Starting {agent.name}…</p>
          )
        ) : (
          <>
            <ConversationMessageStream messages={messages} status={status} />
            {onSendToAgent !== undefined && lastReply !== undefined && !busy && (
              <Button
                className="self-start"
                onClick={() => onSendToAgent(messageText(lastReply))}
                size="sm"
                variant="outline"
              >
                <Share />
                Send this to the agent
              </Button>
            )}
          </>
        )}
        {connection.status.state === 'error' && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-destructive text-xs">
            <p>{connection.status.message}</p>
            <Button className="mt-3" onClick={onRestart} size="sm" variant="outline">
              Try again
            </Button>
          </div>
        )}
        <ConversationConnectionPrompts
          messages={messages}
          onToolApproval={addToolApprovalResponse}
        />
      </SideChatLayout>
    </ConversationActionsProvider>
  );
}
